import { browserSupportsWebAuthn, startRegistration } from "@simplewebauthn/browser";
import { useEffect, useState } from "preact/hooks";
import { api, type SendingAlias } from "../api";
import { shortDate } from "../format";
import { AiSettingsLazy } from "./AiLazy";
import { Icon } from "./icons";
import { Notifications } from "./Notifications";
import { OutOfOffice } from "./OutOfOffice";
import { Rules } from "./Rules";

type Passkey = Awaited<ReturnType<typeof api.passkeys>>[number];
type Session = Awaited<ReturnType<typeof api.sessions>>[number];

// Adresses d'autres domaines redirigées vers une adresse de la messagerie : Robin peut écrire
// depuis elles, avec un nom propre. Leur domaine doit être vérifié chez Resend.
function Aliases({ addresses }: { addresses: string[] }) {
  const [aliases, setAliases] = useState<SendingAlias[]>([]);
  const [draft, setDraft] = useState<SendingAlias>({ alias: "", address: "", name: "" });
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.preferences().then((p) => setAliases(p.aliases ?? [])).catch(() => {}); }, []);

  const save = async (a: SendingAlias) => {
    setError(null);
    try {
      const saved = await api.setAlias(a.alias, a.address, a.name);
      setAliases([...aliases.filter((x) => x.alias !== saved.alias), saved].sort((x, y) => x.alias.localeCompare(y.alias)));
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    }
  };
  const remove = async (alias: string) => {
    await api.deleteAlias(alias).catch(() => {});
    setAliases(aliases.filter((x) => x.alias !== alias));
  };
  const select = (value: string, onChange: (v: string) => void) => (
    <select value={value} aria-label="Adresse de réception" onChange={(e) => onChange((e.target as HTMLSelectElement).value)}>
      <option value="" disabled>Redirigée vers…</option>
      {[...new Set([...addresses, value].filter(Boolean))].map((a) => <option value={a}>{a}</option>)}
    </select>
  );

  return (
    <>
      <h3>Adresses d'autres domaines</h3>
      <p class="sub">
        Une adresse d'un autre domaine redirigée vers l'une des vôtres (par exemple par Cloudflare Email
        Routing) : vous pouvez écrire depuis elle, et une réponse à un e-mail qui lui était adressé part
        d'elle. Son domaine doit être vérifié chez Resend.
      </p>
      {aliases.map((a) => (
        <div class="setting">
          <span>{a.alias}</span>
          <div class="ai-key">
            <input value={a.name} placeholder="Nom affiché (sinon le nom ci-dessus)" aria-label={`Nom affiché pour ${a.alias}`}
              onInput={(e) => setAliases(aliases.map((x) => (x.alias === a.alias ? { ...x, name: (e.target as HTMLInputElement).value } : x)))}
              onBlur={() => save(a)} />
            {select(a.address, (v) => save({ ...a, address: v }))}
            <button type="button" class="btn danger" onClick={() => remove(a.alias)}>Retirer</button>
          </div>
        </div>
      ))}
      <form class="ai-key" onSubmit={async (e) => {
        e.preventDefault();
        if (await save(draft)) setDraft({ alias: "", address: "", name: "" });
      }}>
        <input type="email" value={draft.alias} placeholder="dpo@exemple.com" aria-label="Adresse d'un autre domaine"
          onInput={(e) => setDraft({ ...draft, alias: (e.target as HTMLInputElement).value })} />
        <input value={draft.name} placeholder="Nom affiché (facultatif)" aria-label="Nom affiché"
          onInput={(e) => setDraft({ ...draft, name: (e.target as HTMLInputElement).value })} />
        {select(draft.address, (v) => setDraft({ ...draft, address: v }))}
        <button class="btn" disabled={!draft.alias.trim() || !draft.address}>Ajouter</button>
      </form>
      {error && <p class="error" role="alert">{error}</p>}
    </>
  );
}

// Nom affiché et signature de chaque adresse, enregistrés quand le champ perd le focus.
function Sending() {
  const [name, setName] = useState("");
  const [addresses, setAddresses] = useState<string[]>([]);
  const [signatures, setSignatures] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.preferences(), api.folders()]).then(([p, f]) => {
      setName(p.displayName);
      setSignatures(Object.fromEntries(p.signatures.map((x) => [x.address, x.text])));
      setAddresses([...new Set([...f.addresses.map((a) => a.address), ...p.signatures.map((x) => x.address)])].sort());
    }).catch(() => {});
  }, []);

  const done = (what: string) => { setSaved(what); setTimeout(() => setSaved((s) => (s === what ? null : s)), 2000); };

  return (
    <>
      <h2>Expédition</h2>
      <label class="setting">
        <span>Nom affiché à côté de l'adresse</span>
        <input value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)}
          onBlur={() => api.setDisplayName(name).then(() => done("name"))} />
        {saved === "name" && <span class="sub">Enregistré</span>}
      </label>
      <p class="sub">Signature ajoutée quand l'adresse est choisie comme expéditeur.</p>
      {addresses.map((a) => (
        <label class="setting">
          <span>{a}</span>
          <textarea rows={3} value={signatures[a] ?? ""} placeholder="Aucune signature"
            onInput={(e) => setSignatures({ ...signatures, [a]: (e.target as HTMLTextAreaElement).value })}
            onBlur={() => api.setSignature(a, signatures[a] ?? "").then(() => done(a))} />
          {saved === a && <span class="sub">Enregistrée</span>}
        </label>
      ))}
      <Aliases addresses={addresses} />
    </>
  );
}

export function Settings({ onBack, onLogout, notify }: { onBack: () => void; onLogout: () => void; notify: (text: string) => void }) {
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = () => Promise.all([api.passkeys().then(setPasskeys), api.sessions().then(setSessions)]);
  useEffect(() => { load(); }, []);

  async function addPasskey() {
    setError(null);
    try {
      const optionsJSON = await api.registerPasskeyOptions();
      const response = await startRegistration({ optionsJSON });
      await api.registerPasskeyVerify(response);
      await load();
    } catch (err) {
      const name = (err as Error).name;
      setError(name === "NotAllowedError" ? "Enregistrement annulé." : name === "InvalidStateError" ? "Cet appareil a déjà une passkey." : (err as Error).message);
    }
  }

  return (
    <section class="reader">
      <div class="reader-head">
        <button class="icon-btn" aria-label="Retour" onClick={onBack}><Icon name="back" /></button>
        <strong>Réglages</strong>
      </div>
      <div class="settings">
        <Sending />
        <Notifications notify={notify} />
        <Rules notify={notify} />
        <OutOfOffice notify={notify} />
        <AiSettingsLazy notify={notify} />

        <h2>Passkeys</h2>
        <p class="sub">Connexion par empreinte digitale ou verrouillage de l'appareil, sans mot de passe.</p>
        <ul>
          {passkeys.map((p) => (
            <li>
              <Icon name="key" />
              <div class="grow">
                <div>{p.device ?? "Appareil"}</div>
                <div class="sub">Ajoutée le {shortDate(p.created_at)}{p.last_used ? ` · utilisée le ${shortDate(p.last_used)}` : ""}</div>
              </div>
              <button class="btn danger" onClick={async () => { await api.deletePasskey(p.id); load(); }}>Retirer</button>
            </li>
          ))}
        </ul>
        {browserSupportsWebAuthn()
          ? <p><button class="btn primary" onClick={addPasskey}>Ajouter une passkey sur cet appareil</button></p>
          : <p class="sub">Ce navigateur ne prend pas en charge les passkeys.</p>}
        {error && <p class="error" role="alert">{error}</p>}

        <h2>Appareils connectés</h2>
        <ul>
          {sessions.map((s) => (
            <li>
              <div class="grow">
                <div>{s.device ?? "Appareil"}{s.current ? " · cet appareil" : ""}</div>
                <div class="sub">Dernière activité : {shortDate(s.last_seen)}</div>
              </div>
              {!s.current && <button class="btn danger" onClick={async () => { await api.deleteSession(s.id); load(); }}>Déconnecter</button>}
            </li>
          ))}
        </ul>
        <p><button class="btn" onClick={async () => { await api.logout(); onLogout(); }}>Se déconnecter de cet appareil</button></p>
      </div>
    </section>
  );
}
